import moment from 'moment';
import { useMemo } from 'react';
import IDemoConfig from '../types/IDemoConfig';

const defaultDemoConfig: IDemoConfig = {
  enabled: false,
  uploadAllowed: true,
  dropboxAllowed: true,
  analysisAllowed: true,
  analysisNextAttemptAt: null,
  reportAllowed: true,
  reportNextAttemptAt: null,
};

const isFutureTimestamp = (timestamp: string | null): boolean => {
  if (!timestamp) {
    return false;
  }
  return moment(timestamp).isAfter(moment());
};

const getCooldownMessage = (timestamp: string | null): string | null => {
  if (!timestamp || !isFutureTimestamp(timestamp)) {
    return null;
  }
  return `Available again at ${moment(timestamp).format('YYYY-MM-DD HH:mm')}`;
};

const useDemoRestrictions = (demoConfig: IDemoConfig | undefined) => {
  const config = demoConfig ?? defaultDemoConfig;

  return useMemo(() => {
    const isDemoMode = config.enabled;
    const canUpload = !isDemoMode || config.uploadAllowed;
    const canUseDropbox = !isDemoMode || config.dropboxAllowed;
    const analysisCooldownUntil = isDemoMode ? config.analysisNextAttemptAt : null;
    const reportCooldownUntil = isDemoMode ? config.reportNextAttemptAt : null;
    const canCreateAnalysis =
      !isDemoMode || (config.analysisAllowed && !isFutureTimestamp(config.analysisNextAttemptAt));
    const canGenerateReport =
      !isDemoMode || (config.reportAllowed && !isFutureTimestamp(config.reportNextAttemptAt));

    return {
      isDemoMode,
      canUpload,
      canUseDropbox,
      canCreateAnalysis,
      canGenerateReport,
      analysisCooldownUntil,
      reportCooldownUntil,
      analysisCooldownMessage: getCooldownMessage(config.analysisNextAttemptAt),
      reportCooldownMessage: getCooldownMessage(config.reportNextAttemptAt),
      uploadBlockedMessage: isDemoMode && !config.uploadAllowed ? 'Upload is not available in demo mode.' : null,
      dropboxBlockedMessage:
        isDemoMode && !config.dropboxAllowed ? 'Dropbox is not available in demo mode.' : null,
      analysisBlockedMessage:
        isDemoMode && !canCreateAnalysis
          ? getCooldownMessage(config.analysisNextAttemptAt) || 'Creating analyses is not available in demo mode.'
          : null,
      reportBlockedMessage:
        isDemoMode && !canGenerateReport
          ? getCooldownMessage(config.reportNextAttemptAt) || 'Generating reports is not available in demo mode.'
          : null,
    };
  }, [config]);
};

export default useDemoRestrictions;
